import React, { useState, useEffect, useRef } from 'react';
import { loggingAPI, EnhancedLogEntry, LogFilter } from '../../services/api';
import { io, Socket } from 'socket.io-client';
import './LoggingPanel.css';

// Enhanced log categories with colors
const LOG_CATEGORIES = {
  database: {
    label: 'Database',
    color: '#10b981', // Green
    icon: '🗃️'
  },
  security: {
    label: 'Security',
    color: '#f59e0b', // Orange
    icon: '🔒'
  },
  api: {
    label: 'API Calls',
    color: '#3b82f6', // Blue
    icon: '🌐'
  },
  errors: {
    label: 'Errors',
    color: '#ef4444', // Red
    icon: '❌'
  },
  discord: {
    label: 'Discord Bot',
    color: '#5865F2', // Discord blurple
    icon: '🤖'
  },
  general: {
    label: 'General',
    color: '#6b7280', // Gray
    icon: '📝'
  }
} as const;

type LogCategory = keyof typeof LOG_CATEGORIES;

const LoggingPanel: React.FC = () => {
  // Enhanced state management
  const [activeCategory, setActiveCategory] = useState<LogCategory>('general');
  const [logs, setLogs] = useState<EnhancedLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<EnhancedLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [realTimeEnabled, setRealTimeEnabled] = useState(false);

  // Search and filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({
    from: '',
    to: ''
  });
  const [userFilter, setUserFilter] = useState('');

  const socketRef = useRef<Socket | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  // Helper functions
  const cleaned_message = (message: string): string => {
    return message
      .replace(/\{"date".*?"msg":\s*"/g, '')
      .replace(/"\s*,\s*"meta":.*$/g, '')
      .replace(/^"(.*)"$/g, '$1')
      .substring(0, 200);
  };

  const parseLogContent = (content: string[], category: LogCategory): EnhancedLogEntry[] => {
    return content.map((line, index) => {
      try {
        const parsed = JSON.parse(line);
        return {
          id: `${category}-${index}`,
          timestamp: parsed.date || new Date().toISOString(),
          level: parsed.level || 'info',
          category,
          message: cleaned_message(parsed.msg || line),
          metadata: parsed.meta,
          user: parsed.meta?.username || parsed.meta?.user,
          ip: parsed.meta?.ip
        };
      } catch {
        return {
          id: `${category}-${index}`,
          timestamp: new Date().toISOString(),
          level: 'info' as const,
          category,
          message: cleaned_message(line),
        };
      }
    }).reverse(); // Show newest first
  };

  const loadCategoryLogs = async (category: LogCategory) => {
    setLoading(true);
    try {
      // Use the new enhanced API endpoint
      const response = await loggingAPI.getCategoryLogs(category, {
        limit: 100,
        search: searchQuery || undefined,
        user: userFilter || undefined,
        startDate: dateFilter.from || undefined,
        endDate: dateFilter.to || undefined
      });

      setLogs(response.logs);
    } catch (error) {
      console.error('Error loading logs:', error);
      // Fallback to old method for backward compatibility
      try {
        const files = await loggingAPI.getLogFiles();
        const categoryFiles = files.filter(file => file.name.includes(category));

        if (categoryFiles.length > 0) {
          const content = await loggingAPI.getLogContent(categoryFiles[0].name, 100);
          const parsedLogs = parseLogContent(content, category);
          setLogs(parsedLogs);
        } else {
          setLogs([]);
        }
      } catch (fallbackError) {
        console.error('Fallback error:', fallbackError);
        setLogs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category: LogCategory) => {
    setActiveCategory(category);
    loadCategoryLogs(category);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setDateFilter({ from: '', to: '' });
    setUserFilter('');
  };

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'error': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'success': return '#10b981';
      case 'security': return '#8b5cf6';
      case 'database': return '#06b6d4';
      default: return '#6b7280';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  // Initialize WebSocket connection
  useEffect(() => {
    const SOCKET_URL = import.meta.env.MODE === 'production'
      ? window.location.origin
      : (import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001');

    socketRef.current = io(SOCKET_URL, {
      autoConnect: false
    });

    socketRef.current.on('log-update', (data: { category: LogCategory; log: EnhancedLogEntry }) => {
      if (realTimeEnabled && data.category === activeCategory) {
        setLogs(prev => [data.log, ...prev]);
        if (autoScrollRef.current) {
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [activeCategory, realTimeEnabled]);

  // Enhanced filtering logic
  useEffect(() => {
    let filtered = logs.filter(log => log.category === activeCategory);

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (log.user && log.user.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (log.ip && log.ip.includes(searchQuery))
      );
    }

    // Apply date filter
    if (dateFilter.from) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= new Date(dateFilter.from));
    }
    if (dateFilter.to) {
      filtered = filtered.filter(log => new Date(log.timestamp) <= new Date(dateFilter.to));
    }

    // Apply user filter
    if (userFilter) {
      filtered = filtered.filter(log =>
        log.user && log.user.toLowerCase().includes(userFilter.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [logs, activeCategory, searchQuery, dateFilter, userFilter]);

  // Load initial category
  useEffect(() => {
    loadCategoryLogs(activeCategory);
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time toggle
  useEffect(() => {
    if (realTimeEnabled && socketRef.current) {
      socketRef.current.connect();
      socketRef.current.emit('subscribe-logs', activeCategory);
    } else if (socketRef.current) {
      socketRef.current.disconnect();
    }
  }, [realTimeEnabled, activeCategory]);

  // Export logs functionality
  const exportLogs = () => {
    const dataStr = JSON.stringify(filteredLogs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeCategory}-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  return (
    <div className="logging-panel">
      <div className="logging-header">
        <h2>📊 System Logs</h2>
        <div className="logging-controls">
          <button
            onClick={() => setRealTimeEnabled(!realTimeEnabled)}
            className={`real-time-toggle ${realTimeEnabled ? 'active' : ''}`}
          >
            {realTimeEnabled ? '⏹️ Stop' : '▶️ Live'}
          </button>
          <button onClick={exportLogs} className="export-button">
            📤 Export
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="log-categories">
        {Object.entries(LOG_CATEGORIES).map(([key, config]) => (
          <button
            key={key}
            onClick={() => handleCategoryChange(key as LogCategory)}
            className={`category-tab ${activeCategory === key ? 'active' : ''}`}
            style={{
              '--category-color': config.color
            } as React.CSSProperties}
          >
            <span className="category-icon">{config.icon}</span>
            <span className="category-label">{config.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="log-filters">
        <div className="filter-row">
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />

          <input
            type="text"
            placeholder="Filter by user..."
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="user-filter"
          />
        </div>

        <div className="filter-row">
          <div className="date-filters">
            <input
              type="datetime-local"
              value={dateFilter.from}
              onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
              className="date-input"
            />
            <span>to</span>
            <input
              type="datetime-local"
              value={dateFilter.to}
              onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
              className="date-input"
            />
          </div>

          <button onClick={resetFilters} className="reset-filters">
            🔄 Reset
          </button>
        </div>
      </div>

      {/* Log Results */}
      <div className="log-results">
        <div className="results-header">
          <span className="results-count">
            {filteredLogs.length} entries in {LOG_CATEGORIES[activeCategory].label}
          </span>
          <label className="auto-scroll-toggle">
            <input
              type="checkbox"
              checked={autoScrollRef.current}
              onChange={(e) => autoScrollRef.current = e.target.checked}
            />
            Auto-scroll
          </label>
        </div>

        <div
          ref={logContainerRef}
          className={`log-container ${loading ? 'loading' : ''}`}
        >
          {loading ? (
            <div className="logging-loading-state">
              <div className="logging-spinner-circle"></div>
              <p>Loading logs...</p>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="no-logs">
              <p>No logs found for the current filters</p>
            </div>
          ) : (
            <div className="log-entries">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className={`log-entry level-${log.level}`}
                >
                  <div className="log-header">
                    <span
                      className="log-level"
                      style={{ color: getLevelColor(log.level) }}
                    >
                      {log.level.toUpperCase()}
                    </span>
                    <span className="log-timestamp">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    {log.user && (
                      <span className="log-user">👤 {log.user}</span>
                    )}
                    {log.ip && (
                      <span className="log-ip">🌐 {log.ip}</span>
                    )}
                  </div>
                  <div className="log-message">
                    {log.message}
                  </div>
                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <details className="log-metadata">
                      <summary>📋 Details</summary>
                      <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoggingPanel;
